import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box } from '@mui/material';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import SimplePageHero from '../marketing/components/SimplePageHero';
import { TEMPLATES } from '../marketing/data';
import { marketingColors as C, marketingFonts as F } from '../theme_landing_page';

const fmtBg: Record<string, string> = { PDF: C.formatBadge.PDF, XLSX: C.formatBadge.XLSX, DOCX: C.formatBadge.DOCX };

const TemplatesPage: React.FC = () => {
  useMarketingFonts();
  useSeoHead({
    title: 'Free Landlord Templates — Checklists & Spreadsheets | MillOS',
    description: 'Download free UK landlord templates: compliance checklist, certificate renewal calendar, rental income spreadsheet and property expense tracker in PDF and XLSX.',
    canonicalPath: '/templates',
  });

  return (
    <Box className="lv-marketing" sx={{ background: C.white, color: C.ink, fontFamily: F.body, fontSize: 16, lineHeight: 1.5, overflowX: 'hidden' }}>
      <MarketingNav />
      <SimplePageHero
        eyebrow="Free downloads"
        title={<>Landlord <Box component="em" sx={{ fontStyle: 'italic' }}>Templates</Box></>}
        body="Battle-tested checklists and spreadsheets — download free, or let MillOS do it all for you."
      />
      <Box component="section" sx={{ padding: { xs: '48px 16px 64px', md: '56px 52px 96px' } }}>
        <Box sx={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '22px' }}>
          {TEMPLATES.map((t) => (
            <Box key={t.slug} sx={{ display: 'flex', flexDirection: 'column', gap: '18px', background: C.panel, borderRadius: '24px', padding: '32px', height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ width: 48, height: 48, borderRadius: '13px', background: C.ink, display: 'grid', placeItems: 'center' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.white} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                </Box>
                <Box sx={{ background: fmtBg[t.format] || C.accent, color: C.white, borderRadius: '32px', padding: '5px 13px', fontSize: 11, fontWeight: 600, letterSpacing: '0.06rem' }}>
                  {t.format}
                </Box>
              </Box>
              <Box component="h2" sx={{ fontFamily: F.display, fontSize: 26, lineHeight: 1.1, margin: 0 }}>{t.name}</Box>
              <Box sx={{ color: C.body, fontSize: 14.5, lineHeight: 1.55 }}>{t.intro}</Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {t.sections.map((s) => (
                  <Box key={s.heading} sx={{ background: C.white, border: '1px solid rgba(0,0,0,0.08)', borderRadius: '32px', padding: '4px 12px', fontSize: 12, color: C.body }}>
                    {s.heading}
                  </Box>
                ))}
              </Box>
              <Box
                component={RouterLink}
                to="/register"
                sx={{ display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', justifyContent: 'center', background: C.inkSoft, color: C.white, borderRadius: '32px', padding: '11px 20px', fontSize: 15, marginTop: 'auto', '&:hover': { background: C.accent } }}
              >
                Download {t.format}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      <MarketingFooter />
    </Box>
  );
};

export default TemplatesPage;
